import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CreditCard, FileText, HelpCircle, Loader2 } from "lucide-react"
import { useLocation, useNavigate, useSearchParams } from "react-router"

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { getTodayDateOnly } from "@f2e/ui/lib/date"

import { BILLING_OVERVIEW_REFRESH_EVENT, FREEMIUM_PLAN_CODE } from "@/constants"
import { AlertBox } from "@/components"
import { useToast } from "@/hooks"
import {
  getApiErrorPresentationProps,
  getUserFacingApiErrorToastProps,
  normalizeApiError,
} from "@/lib/apiErrors"
import {
  FetchStatus,
  useChangePlanApi,
  useCancelScheduledPlanChangeApi,
  useCreateExtraCreditsCheckoutSessionApi,
  useCreateBillingPortalSessionApi,
  useCreateSubscriptionCheckoutSessionApi,
  useGetLatestInvoiceHostedPageApi,
  useGetBillingPlansApi,
  useGetOrganizationCreditUsageApi,
  useGetOrganizationLimitUsageApi,
  useGetBillingSubscriptionApi,
  useGetOrganizationMetricsApi,
  transformBillingOverview,
  transformCreditUsage,
  transformOrganizationLimitUsage,
  transformPlan,
  transformOrganizationStats,
  CreditUsage,
  OrganizationLimitUsageItem,
  Plan,
} from "@/hooks/api"
import { useStore } from "@/store"
import {
  BillingRulesDialog,
  CreditUsageRecordsModal,
  CreditUsageTrendChartCard,
  UsageBar,
} from "@/components/Billing"
import { OrganizationStatsData } from "@/types/metrics"

const SUBSCRIPTION_CHECKOUT_IN_PROGRESS_ERROR_CODE = 14112
const PLAN_UPGRADE_IN_PROGRESS_ERROR_CODE = 14113
const PLAN_DOWNGRADE_IN_PROGRESS_ERROR_CODE = 14114
const EXTRA_CREDITS_CHECKOUT_IN_PROGRESS_ERROR_CODE = 14115

const parseDecimal = (value?: string | number | null) => {
  if (!value) return 0
  const parsed = Number(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

const formatBytes = (bytes: number) => {
  if (!bytes) return "0 B"
  const sizes = ["B", "KB", "MB", "GB", "TB"]
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

export const formatDate = (
  value?: string | null,
  locale?: string,
  options?: Intl.DateTimeFormatOptions
) => {
  if (!value) return "-"
  const dateOnlyMatch = value.match(DATE_ONLY_PATTERN)
  if (dateOnlyMatch) {
    const utcDate = new Date(
      Date.UTC(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3])
      )
    )
    return new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: "UTC",
    }).format(utcDate)
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleDateString(locale, options)
}

const getBillingOperationWarningCopy = (
  errorCode: number | undefined,
  t: ReturnType<typeof useTranslation>["t"]
) => {
  switch (errorCode) {
    case SUBSCRIPTION_CHECKOUT_IN_PROGRESS_ERROR_CODE:
      return {
        title: t("billing.plan.errors.checkoutInProgressTitle"),
        description: t("billing.plan.errors.checkoutInProgressDescription"),
      }
    case PLAN_UPGRADE_IN_PROGRESS_ERROR_CODE:
      return {
        title: t("billing.plan.errors.upgradeInProgressTitle"),
        description: t("billing.plan.errors.upgradeInProgressDescription"),
      }
    case PLAN_DOWNGRADE_IN_PROGRESS_ERROR_CODE:
      return {
        title: t("billing.plan.errors.downgradeInProgressTitle"),
        description: t("billing.plan.errors.downgradeInProgressDescription"),
      }
    case EXTRA_CREDITS_CHECKOUT_IN_PROGRESS_ERROR_CODE:
      return {
        title: t("billing.plan.errors.extraCreditsCheckoutInProgressTitle"),
        description: t(
          "billing.plan.errors.extraCreditsCheckoutInProgressDescription"
        ),
      }
    default:
      return null
  }
}

const formatCredits = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

const formatCreditUsageLabel = (
  date: string,
  granularity: "daily" | "weekly",
  totalPoints: number
) => {
  const parsedDate = new Date(`${date}T00:00:00`)
  if (Number.isNaN(parsedDate.getTime())) return date

  const formatter =
    granularity === "weekly" || totalPoints > 7
      ? new Intl.DateTimeFormat(undefined, {
          month: "short",
          day: "numeric",
        })
      : new Intl.DateTimeFormat(undefined, {
          weekday: "short",
        })

  return formatter.format(parsedDate)
}

const buildCreditUsageChartData = (usage: CreditUsage | null) => {
  if (!usage || !usage.hasData) return []
  return usage.points.map((point) => ({
    date: point.date,
    label: formatCreditUsageLabel(
      point.date,
      usage.granularity,
      usage.points.length
    ),
    value: point.value,
  }))
}

interface PlanLimitUsageItem {
  key: string
  title: string
  used: number
  limit?: number | null
  unit: "count" | "bytes"
  atLimit: boolean
  valueLabel: string
}

interface PlanLimitUsageBarProps {
  item: PlanLimitUsageItem
}

const PlanLimitUsageBar = ({ item }: PlanLimitUsageBarProps) => {
  const percent =
    item.limit == null
      ? 0
      : item.limit <= 0
        ? 100
        : Math.min(100, (Math.max(0, item.used) / item.limit) * 100)
  const barColor =
    item.atLimit || percent >= 100
      ? "bg-red-500"
      : percent >= 80
        ? "bg-yellow-500"
        : "bg-green-500"

  return (
    <div className="space-y-2 rounded-lg border border-muted/60 bg-muted/20 p-4">
      <div className="flex items-start justify-between gap-3 text-sm">
        <div className="font-medium text-foreground">{item.title}</div>
        <span className="font-medium text-muted-foreground whitespace-nowrap">
          {item.valueLabel}
        </span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-muted">
        <div
          className={`${barColor} h-2.5 rounded-full transition-all`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

const getCreditGranularityFromQuery = (
  value: string | null
): "daily" | "weekly" => (value === "weekly" ? "weekly" : "daily")

const getCreditUsageFiltersFromSearchParams = (
  searchParams: URLSearchParams
) => ({
  granularity: getCreditGranularityFromQuery(
    searchParams.get("credit_granularity")
  ),
  workspaceId: searchParams.get("credit_workspace_id") || "all",
  startDate: searchParams.get("credit_start_date") || "",
  endDate: searchParams.get("credit_end_date") || "",
  hasCustomFilters: Boolean(
    searchParams.get("credit_workspace_id") ||
    searchParams.get("credit_start_date") ||
    searchParams.get("credit_end_date") ||
    searchParams.get("credit_granularity")
  ),
})

const OrganizationPlanTab = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const creditQueryFilters = useMemo(
    () => getCreditUsageFiltersFromSearchParams(searchParams),
    [searchParams]
  )
  const { alertToast, successToast } = useToast()
  const [currentOrganization, workspaces] = useStore((state) => [
    state.currentOrganization,
    state.workspaces,
  ])
  const [downgradeWarningOpen, setDowngradeWarningOpen] = useState(false)
  const [downgradeWarningMessage, setDowngradeWarningMessage] = useState("")
  const [planChangeConfirmOpen, setPlanChangeConfirmOpen] = useState(false)
  const [pendingPlanAction, setPendingPlanAction] = useState<Plan | null>(null)
  const [cancelScheduledConfirmOpen, setCancelScheduledConfirmOpen] =
    useState(false)
  const [extraCreditsConfirmOpen, setExtraCreditsConfirmOpen] = useState(false)
  const [pendingExtraCredits, setPendingExtraCredits] = useState<number | null>(
    null
  )
  const [rulesOpen, setRulesOpen] = useState(false)
  const [isChangingPlan, setIsChangingPlan] = useState(false)
  const [isPollingPlanChange, setIsPollingPlanChange] = useState(false)
  const [pollingTargetPlanId, setPollingTargetPlanId] = useState<number | null>(
    null
  )
  const [pollingAttempt, setPollingAttempt] = useState(0)
  const [rulesSection, setRulesSection] = useState<"extra_credits" | "plans">(
    "extra_credits"
  )
  const [creditGranularity, setCreditGranularity] = useState<
    "daily" | "weekly"
  >(creditQueryFilters.granularity)
  const [isCreditUsageExpanded, setIsCreditUsageExpanded] = useState(
    () => location.hash === "#credit-usage-details"
  )
  const [creditWorkspaceId, setCreditWorkspaceId] = useState(
    creditQueryFilters.workspaceId
  )
  const [creditStartDate, setCreditStartDate] = useState(
    creditQueryFilters.startDate
  )
  const [creditEndDate, setCreditEndDate] = useState(creditQueryFilters.endDate)
  const [hasInitializedCreditRange, setHasInitializedCreditRange] = useState(
    creditQueryFilters.hasCustomFilters
  )
  const [selectedCreditUsageDate, setSelectedCreditUsageDate] = useState<
    string | null
  >(null)
  const [creditUsageModalOpen, setCreditUsageModalOpen] = useState(false)
  const [organizationStats, setOrganizationStats] =
    useState<OrganizationStatsData>({
      userCount: 0,
      workspaceCount: 0,
      connectionCount: 0,
      contextObjectCount: 0,
      contextSize: 0,
    })

  const {
    data: plansResp,
    error: plansError,
    fetchFn: fetchPlans,
  } = useGetBillingPlansApi()
  const {
    data: overviewResp,
    error: overviewError,
    fetchFn: fetchOverview,
  } = useGetBillingSubscriptionApi()
  const {
    data: creditUsageResp,
    error: creditUsageError,
    status: creditUsageStatus,
    fetchFn: fetchCreditUsage,
  } = useGetOrganizationCreditUsageApi(currentOrganization?.id || "")
  const {
    data: limitUsageResp,
    error: limitUsageError,
    fetchFn: fetchLimitUsage,
  } = useGetOrganizationLimitUsageApi(currentOrganization?.id || "")
  const {
    data: metricsResp,
    error: metricsError,
    fetchFn: fetchMetrics,
  } = useGetOrganizationMetricsApi(currentOrganization?.id || "")
  const { fetchFn: createSubscriptionSession } =
    useCreateSubscriptionCheckoutSessionApi()
  const { fetchFn: createExtraCreditsSession } =
    useCreateExtraCreditsCheckoutSessionApi()
  const {
    fetchFn: createBillingPortalSession,
    status: billingPortalSessionStatus,
  } = useCreateBillingPortalSessionApi()
  const {
    fetchFn: getLatestInvoiceHostedPage,
    status: latestInvoiceHostedPageStatus,
  } = useGetLatestInvoiceHostedPageApi()
  const { fetchFn: changePlan } = useChangePlanApi()
  const { fetchFn: cancelScheduledChange } = useCancelScheduledPlanChangeApi()

  const organizationId = currentOrganization?.id
  const organizationWorkspaces = useMemo(
    () =>
      (workspaces ?? [])
        .filter((workspace) => workspace.organizationId === organizationId)
        .sort((left, right) => left.name.localeCompare(right.name)),
    [organizationId, workspaces]
  )

  const overviewSearchParams = useMemo(() => {
    if (!organizationId) return null
    return new URLSearchParams({ organization_id: organizationId })
  }, [organizationId])
  const creditUsage = useMemo(
    () =>
      creditUsageResp?.data ? transformCreditUsage(creditUsageResp.data) : null,
    [creditUsageResp?.data]
  )
  const creditUsageChartData = useMemo(
    () => buildCreditUsageChartData(creditUsage),
    [creditUsage]
  )
  const canDrillIntoCreditUsage = creditGranularity === "daily"

  useEffect(() => {
    if (!organizationId) return
    fetchPlans()
    fetchOverview({
      searchParams: new URLSearchParams({ organization_id: organizationId }),
    })
    fetchLimitUsage()
    const today = getTodayDateOnly()
    fetchMetrics({
      searchParams: new URLSearchParams({
        start_date: today,
        end_date: today,
        with_status: "true",
      }),
    })
  }, [organizationId, fetchPlans, fetchOverview, fetchLimitUsage, fetchMetrics])

  useEffect(() => {
    setCreditGranularity(creditQueryFilters.granularity)
    setCreditWorkspaceId(creditQueryFilters.workspaceId)
    setCreditStartDate(creditQueryFilters.startDate)
    setCreditEndDate(creditQueryFilters.endDate)
    setHasInitializedCreditRange(creditQueryFilters.hasCustomFilters)
  }, [organizationId, creditQueryFilters])

  useEffect(() => {
    if (creditWorkspaceId === "all") return
    if (
      organizationWorkspaces.some(
        (workspace) => workspace.id === creditWorkspaceId
      )
    )
      return
    setCreditWorkspaceId("all")
  }, [creditWorkspaceId, organizationWorkspaces])

  useEffect(() => {
    if (!organizationId) return
    const params = new URLSearchParams({
      granularity: creditGranularity,
    })
    if (creditWorkspaceId !== "all") {
      params.set("workspace_id", creditWorkspaceId)
    }
    if (creditStartDate) {
      params.set("start_date", creditStartDate)
    }
    if (creditEndDate) {
      params.set("end_date", creditEndDate)
    }
    fetchCreditUsage({
      searchParams: params,
    })
  }, [
    organizationId,
    creditGranularity,
    creditWorkspaceId,
    creditStartDate,
    creditEndDate,
    fetchCreditUsage,
  ])

  useEffect(() => {
    if (plansError) {
      alertToast({
        ...getUserFacingApiErrorToastProps(plansError, t, {
          title: t("billing.plan.errors.loadFailedTitle"),
          fallbackDescription: t("billing.plan.errors.loadFailedDescription"),
        }),
        showSupportAction: true,
      })
    }
  }, [plansError, alertToast, t])

  useEffect(() => {
    if (overviewError) {
      alertToast({
        ...getUserFacingApiErrorToastProps(overviewError, t, {
          title: t("billing.plan.errors.loadFailedTitle"),
          fallbackDescription: t("billing.plan.errors.loadFailedDescription"),
        }),
        showSupportAction: true,
      })
    }
  }, [overviewError, alertToast, t])

  useEffect(() => {
    if (metricsError) {
      alertToast({
        ...getUserFacingApiErrorToastProps(metricsError, t, {
          title: t("billing.plan.errors.loadFailedTitle"),
          fallbackDescription: t("billing.plan.errors.loadFailedDescription"),
        }),
        showSupportAction: true,
      })
    }
  }, [metricsError, alertToast, t])

  useEffect(() => {
    if (limitUsageError) {
      alertToast({
        ...getUserFacingApiErrorToastProps(limitUsageError, t, {
          title: t("billing.plan.errors.loadFailedTitle"),
          fallbackDescription: t("billing.plan.errors.loadFailedDescription"),
        }),
        showSupportAction: true,
      })
    }
  }, [limitUsageError, alertToast, t])

  useEffect(() => {
    if (creditUsageError) {
      alertToast({
        ...getUserFacingApiErrorToastProps(creditUsageError, t, {
          title: t("billing.plan.errors.loadFailedTitle"),
          fallbackDescription: t("billing.plan.errors.loadFailedDescription"),
        }),
        showSupportAction: true,
      })
    }
  }, [creditUsageError, alertToast, t])

  useEffect(() => {
    if (!creditUsage || hasInitializedCreditRange) return
    setCreditStartDate(creditUsage.rangeStart)
    setCreditEndDate(creditUsage.rangeEnd)
    setHasInitializedCreditRange(true)
  }, [creditUsage, hasInitializedCreditRange])

  useEffect(() => {
    if (location.hash !== "#credit-usage-details") return
    setIsCreditUsageExpanded(true)
    const frame = window.requestAnimationFrame(() => {
      document
        .getElementById("credit-usage-details")
        ?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [location.hash, creditUsageStatus])

  const plans = useMemo(
    () => (plansResp?.data ?? []).map(transformPlan),
    [plansResp?.data]
  )
  const overview = overviewResp?.data
    ? transformBillingOverview(overviewResp.data)
    : null
  const limitUsage = limitUsageResp?.data
    ? transformOrganizationLimitUsage(limitUsageResp.data)
    : null
  const subscription = overview?.subscription ?? null
  const stats = metricsResp?.data?.status
  useEffect(() => {
    if (!stats) return
    setOrganizationStats(transformOrganizationStats(stats))
  }, [stats])
  const currentPlan = useMemo(
    () => plans.find((plan) => plan.id === subscription?.planId) ?? null,
    [plans, subscription?.planId]
  )
  const pendingPlan = useMemo(
    () => plans.find((plan) => plan.id === subscription?.pendingPlanId) ?? null,
    [plans, subscription?.pendingPlanId]
  )
  const isDbStagedFreemiumFallback =
    pendingPlan?.code === FREEMIUM_PLAN_CODE &&
    subscription?.managementMode === "self_serve" &&
    !subscription?.stripeSubscriptionId &&
    Boolean(subscription?.pendingEffectiveDate)

  useEffect(() => {
    if (!organizationId || !subscription?.planId) return
    fetchLimitUsage({ preserveData: true })
  }, [organizationId, subscription?.planId, fetchLimitUsage])

  const creditsBalance = parseDecimal(overview?.creditsBalance)
  const extraCreditsBalance = parseDecimal(overview?.extraCreditsBalance)
  const monthlyLimit = parseDecimal(overview?.monthlyCreditsLimit ?? null)
  const packOptions = currentPlan?.extraCreditPackOptions ?? []
  const extraCreditUnitPrice = parseDecimal(
    currentPlan?.extraCreditUnitPriceUsdPerCredit ?? null
  )
  const isFreemium = currentPlan?.code === FREEMIUM_PLAN_CODE
  const isExtraCreditsCheckoutAllowed =
    !!subscription && subscription.status !== "canceled"
  const canBuyExtraCredits =
    !isFreemium && packOptions.length > 0 && isExtraCreditsCheckoutAllowed
  const renewalDate = formatDate(subscription?.currentPeriodEnd)
  const billingCycleRange = useMemo(() => {
    if (!subscription?.currentPeriodStart || !subscription?.currentPeriodEnd) {
      return null
    }
    return t("billing.plan.currentCycleRange", {
      start: formatDate(subscription.currentPeriodStart),
      end: formatDate(subscription.currentPeriodEnd),
    })
  }, [subscription?.currentPeriodStart, subscription?.currentPeriodEnd, t])
  const planLimitUsageItems = useMemo<PlanLimitUsageItem[]>(() => {
    if (!limitUsage) return []

    const labelByKey: Record<string, string> = {
      workspaces: t("billing.plan.limitUsageLabels.workspaces"),
      users: t("billing.plan.limitUsageLabels.users"),
      channels: t("billing.plan.limitUsageLabels.channels"),
      knowledge_base_storage: t(
        "billing.plan.limitUsageLabels.knowledgeBaseStorage"
      ),
    }

    return limitUsage.items.map((item: OrganizationLimitUsageItem) => {
      const usedLabel =
        item.unit === "bytes"
          ? formatBytes(item.used)
          : formatCredits(item.used)
      const limitLabel =
        item.limit == null
          ? t("billing.plan.unlimited")
          : item.unit === "bytes"
            ? formatBytes(item.limit)
            : formatCredits(item.limit)

      return {
        key: item.key,
        title: labelByKey[item.key] ?? item.key,
        used: item.used,
        limit: item.limit,
        unit: item.unit,
        atLimit: item.atLimit,
        valueLabel: t("billing.plan.limitUsageValue", {
          used: usedLabel,
          limit: limitLabel,
        }),
      }
    })
  }, [limitUsage, t])

  const finishPlanChangePolling = useCallback(() => {
    setIsPollingPlanChange(false)
    setPollingTargetPlanId(null)
    setPollingAttempt(0)
    window.dispatchEvent(new Event(BILLING_OVERVIEW_REFRESH_EVENT))
    successToast({
      title: t("billing.plan.changeSuccessTitle"),
      description: t("billing.plan.changeSuccessDescription"),
    })
  }, [successToast, t])

  useEffect(() => {
    if (!isPollingPlanChange || pollingTargetPlanId == null) return
    if (subscription?.planId === pollingTargetPlanId) finishPlanChangePolling()
  }, [
    isPollingPlanChange,
    pollingTargetPlanId,
    subscription?.planId,
    finishPlanChangePolling,
  ])

  useEffect(() => {
    if (!isPollingPlanChange || pollingTargetPlanId == null) return
    if (!overviewSearchParams) return
    if (pollingAttempt >= 4) {
      finishPlanChangePolling()
      return
    }
    fetchOverview({ searchParams: overviewSearchParams, preserveData: true })
    const timer = window.setTimeout(() => setPollingAttempt((a) => a + 1), 1500)
    return () => window.clearTimeout(timer)
  }, [
    isPollingPlanChange,
    pollingTargetPlanId,
    overviewSearchParams,
    pollingAttempt,
    fetchOverview,
    finishPlanChangePolling,
  ])

  const getDowngradeIssues = useCallback(
    (plan: Plan) => {
      if (plan.requiresSalesContact) return []
      const issues: string[] = []
      const workspaceLimit = plan.workspaceLimit
      const endUserLimit = plan.endUserLimit
      const storageLimit = plan.knowledgeBaseStorageBytesLimit

      if (
        workspaceLimit != null &&
        organizationStats.workspaceCount > workspaceLimit
      ) {
        issues.push(
          t("billing.plan.downgradeLimitItem", {
            label: t("billing.plan.limitLabels.workspaces"),
            current: organizationStats.workspaceCount,
            limit: workspaceLimit,
          })
        )
      }

      if (endUserLimit != null && organizationStats.userCount > endUserLimit) {
        issues.push(
          t("billing.plan.downgradeLimitItem", {
            label: t("billing.plan.limitLabels.members"),
            current: organizationStats.userCount,
            limit: endUserLimit,
          })
        )
      }

      if (
        storageLimit != null &&
        organizationStats.contextSize > storageLimit
      ) {
        issues.push(
          t("billing.plan.downgradeLimitItem", {
            label: t("billing.plan.limitLabels.storage"),
            current: formatBytes(organizationStats.contextSize),
            limit: formatBytes(storageLimit),
          })
        )
      }

      return issues
    },
    [organizationStats, t]
  )

  const statusLabel = subscription
    ? t(`billing.plan.statusValues.${subscription.status}`, {
        defaultValue: subscription.status,
      })
    : ""
  const isOperatorManaged = subscription?.managementMode === "operator_managed"
  const isPendingActivation = subscription?.status === "pending_activation"
  const isPendingOperatorTakeover =
    subscription?.pendingManagementMode === "operator_managed"
  const selfServePlanChangeBlocked =
    isOperatorManaged || isPendingActivation || isPendingOperatorTakeover
  const isBusy = isChangingPlan || isPollingPlanChange
  const isOpeningBillingPortal =
    billingPortalSessionStatus === FetchStatus.Loading
  const isOpeningLatestInvoice =
    latestInvoiceHostedPageStatus === FetchStatus.Loading
  const canViewLatestInvoice =
    Boolean(subscription?.canViewLatestInvoice) && !isOperatorManaged
  const disablePlanChanges = isBusy || selfServePlanChangeBlocked
  const selfServeBlockedTitle = isPendingActivation
    ? t("billing.plan.pendingActivationNoticeTitle")
    : isPendingOperatorTakeover
      ? t("billing.plan.pendingTakeoverNoticeTitle")
      : t("billing.plan.operatorManagedNoticeTitle")
  const selfServeBlockedDescription = isPendingActivation
    ? t("billing.plan.pendingActivationNoticeDescription")
    : isPendingOperatorTakeover
      ? t("billing.plan.pendingTakeoverNoticeDescription", {
          date: formatDate(subscription?.pendingTakeoverEffectiveDate),
        })
      : t("billing.plan.operatorManagedNoticeDescription")

  const planChangeConfirmDescription = useMemo(() => {
    if (!pendingPlanAction)
      return t("billing.plan.planChangeConfirmDescription")

    // For first-time paid subscriptions, we redirect to Stripe Checkout (no existing subscription to mutate).
    if (!subscription?.stripeSubscriptionId) {
      return t("billing.plan.planChangeConfirmDescriptionSubscribe")
    }

    const currentCredits = currentPlan?.includedCreditsMonthly ?? 0
    const nextCredits = pendingPlanAction.includedCreditsMonthly ?? 0
    if (nextCredits > currentCredits) {
      return t("billing.plan.planChangeConfirmDescriptionUpgrade")
    }
    if (nextCredits < currentCredits) {
      return t("billing.plan.planChangeConfirmDescriptionDowngrade")
    }
    return t("billing.plan.planChangeConfirmDescription")
  }, [pendingPlanAction, subscription?.stripeSubscriptionId, currentPlan, t])

  const runPlanAction = useCallback(
    async (plan: Plan) => {
      if (!organizationId || disablePlanChanges) return
      const monthlyPrice = plan.prices.find(
        (price) => price.billingPeriod === "monthly"
      )
      const billingPeriod = monthlyPrice?.billingPeriod ?? "monthly"

      try {
        if (subscription?.stripeSubscriptionId) {
          const issues = getDowngradeIssues(plan)
          if (issues.length > 0) {
            setDowngradeWarningMessage(
              t("billing.plan.downgradeLimitDescription", {
                details: issues.join(" · "),
              })
            )
            setDowngradeWarningOpen(true)
            return
          }
          setIsChangingPlan(true)
          await changePlan({
            jsonBody: {
              organization_id: organizationId,
              plan_id: plan.id,
              billing_period: billingPeriod,
              proration_behavior: "always_invoice",
            },
          })
          setPollingTargetPlanId(plan.id)
          setPollingAttempt(0)
          setIsPollingPlanChange(true)
          return
        }

        setIsChangingPlan(true)
        const sessionResp = await createSubscriptionSession({
          jsonBody: {
            organization_id: organizationId,
            plan_id: plan.id,
            billing_period: billingPeriod,
            success_url: window.location.href,
            cancel_url: window.location.href,
            allow_promotion_codes: true,
          },
        })
        const checkoutUrl = sessionResp?.data?.checkout_url
        if (checkoutUrl) {
          window.location.href = checkoutUrl
        } else {
          alertToast({
            title: t("billing.plan.errors.checkoutFailedTitle"),
            description: t("billing.plan.errors.checkoutFailedDescription"),
            showSupportAction: true,
          })
        }
      } catch (e) {
        let title = t("billing.plan.errors.checkoutFailedTitle")
        let description = t("billing.plan.errors.checkoutFailedDescription")
        const normalizedError = normalizeApiError(e)
        const errorPayload = normalizedError.data as
          | { cooldown_minutes?: number }
          | undefined
        const cooldownMinutes = Number(errorPayload?.cooldown_minutes)
        if (normalizedError.messageKey) {
          description = t(normalizedError.messageKey)
        } else if (!Number.isNaN(cooldownMinutes) && cooldownMinutes > 0) {
          title = t("billing.plan.errors.changePlanCooldownTitle")
          description = t("billing.plan.errors.changePlanCooldownDescription", {
            minutes: cooldownMinutes,
          })
        } else {
          const billingWarning = getBillingOperationWarningCopy(
            normalizedError.errorCode ?? undefined,
            t
          )
          if (billingWarning) {
            title = billingWarning.title
            description = billingWarning.description
          } else if (normalizedError.message) {
            description = normalizedError.message
          }
        }
        alertToast({
          title,
          description,
          ...(normalizedError.kind === "permission_denied"
            ? getApiErrorPresentationProps(e)
            : {}),
          showSupportAction: true,
        })
      } finally {
        setIsChangingPlan(false)
      }
    },
    [
      organizationId,
      disablePlanChanges,
      subscription,
      getDowngradeIssues,
      changePlan,
      createSubscriptionSession,
      alertToast,
      t,
    ]
  )

  const handlePlanAction = useCallback(
    (plan: Plan) => {
      if (disablePlanChanges) return
      setPendingPlanAction(plan)
      setPlanChangeConfirmOpen(true)
    },
    [disablePlanChanges]
  )

  const handleContactEnterprise = useCallback(
    (plan: Plan) => {
      const email = plan.contactEmail
      if (!email) {
        alertToast({
          title: t("billing.plan.errors.contactEmailMissingTitle"),
          description: t("billing.plan.errors.contactEmailMissingDescription"),
          showSupportAction: true,
        })
        return
      }

      const subject = encodeURIComponent(
        t("billing.plan.enterpriseContactSubject", {
          plan: plan.displayName,
          org: currentOrganization?.name ?? "",
        })
      )
      const body = encodeURIComponent(
        t("billing.plan.enterpriseContactBody", {
          plan: plan.displayName,
          org: currentOrganization?.name ?? "",
        })
      )

      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`
    },
    [alertToast, currentOrganization?.name, t]
  )

  const handleManagePaymentMethod = useCallback(async () => {
    if (!organizationId) return
    const portalWindow = window.open("about:blank", "_blank")
    if (!portalWindow) {
      alertToast({
        title: t("billing.plan.errors.billingPortalFailedTitle"),
        description: t(
          "billing.plan.errors.billingPortalPopupBlockedDescription"
        ),
      })
      return
    }
    portalWindow.opener = null

    try {
      const sessionResp = await createBillingPortalSession({
        jsonBody: {
          organization_id: organizationId,
          return_url: window.location.href,
        },
      })
      const portalUrl = sessionResp?.data?.portal_url
      if (portalUrl) {
        portalWindow.location.href = portalUrl
        return
      }
      portalWindow.close()
      alertToast({
        title: t("billing.plan.errors.billingPortalFailedTitle"),
        description: t("billing.plan.errors.billingPortalFailedDescription"),
      })
    } catch (e) {
      portalWindow.close()
      let description = t("billing.plan.errors.billingPortalFailedDescription")
      const normalizedError = normalizeApiError(e)
      if (normalizedError.messageKey) {
        description = t(normalizedError.messageKey)
      } else if (normalizedError.message === "No Stripe customer") {
        description = t("billing.plan.errors.noStripeCustomerDescription")
      } else if (normalizedError.message) {
        description = normalizedError.message
      }
      alertToast({
        title: t("billing.plan.errors.billingPortalFailedTitle"),
        description,
        ...(normalizedError.kind === "permission_denied"
          ? getApiErrorPresentationProps(e)
          : {}),
      })
    }
  }, [organizationId, createBillingPortalSession, alertToast, t])

  const handleViewLatestInvoice = useCallback(async () => {
    if (!organizationId) return
    const invoiceWindow = window.open("about:blank", "_blank")
    if (!invoiceWindow) {
      alertToast({
        title: t("billing.plan.errors.invoiceHostedPageFailedTitle"),
        description: t(
          "billing.plan.errors.invoiceHostedPagePopupBlockedDescription"
        ),
      })
      return
    }
    invoiceWindow.opener = null

    try {
      const invoiceResp = await getLatestInvoiceHostedPage({
        searchParams: new URLSearchParams({ organization_id: organizationId }),
      })
      const hostedInvoiceUrl = invoiceResp?.data?.hosted_invoice_url
      if (hostedInvoiceUrl) {
        invoiceWindow.location.href = hostedInvoiceUrl
        return
      }
      invoiceWindow.close()
      alertToast({
        title: t("billing.plan.errors.invoiceHostedPageFailedTitle"),
        description: t(
          "billing.plan.errors.invoiceHostedPageUnavailableDescription"
        ),
      })
    } catch (e) {
      invoiceWindow.close()
      let description = t(
        "billing.plan.errors.invoiceHostedPageFailedDescription"
      )
      const normalizedError = normalizeApiError(e)
      if (normalizedError.messageKey) {
        description = t(normalizedError.messageKey)
      } else if (normalizedError.message === "No latest invoice") {
        description = t("billing.plan.errors.noLatestInvoiceDescription")
      } else if (
        normalizedError.message === "Invoice hosted page is not available"
      ) {
        description = t(
          "billing.plan.errors.invoiceHostedPageUnavailableDescription"
        )
      } else if (normalizedError.message) {
        description = normalizedError.message
      }
      alertToast({
        title: t("billing.plan.errors.invoiceHostedPageFailedTitle"),
        description,
        ...(normalizedError.kind === "permission_denied"
          ? getApiErrorPresentationProps(e)
          : {}),
      })
    }
  }, [organizationId, getLatestInvoiceHostedPage, alertToast, t])

  const confirmPlanAction = useCallback(() => {
    if (!pendingPlanAction) return
    setPlanChangeConfirmOpen(false)
    void runPlanAction(pendingPlanAction)
  }, [pendingPlanAction, runPlanAction])

  const cancelScheduledDowngrade = useCallback(async () => {
    if (!organizationId) return
    try {
      setIsChangingPlan(true)
      await cancelScheduledChange({
        jsonBody: { organization_id: organizationId },
      })
      await fetchOverview({
        searchParams: new URLSearchParams({ organization_id: organizationId }),
        preserveData: true,
      })
      window.dispatchEvent(new Event(BILLING_OVERVIEW_REFRESH_EVENT))
      successToast({
        title: t("billing.plan.cancelScheduledSuccessTitle"),
        description: t("billing.plan.cancelScheduledSuccessDescription"),
      })
    } catch (e) {
      const title = t("billing.plan.errors.checkoutFailedTitle")
      let description = t("billing.plan.errors.checkoutFailedDescription")
      const normalizedError = normalizeApiError(e)
      if (normalizedError.messageKey) {
        description = t(normalizedError.messageKey)
      } else if (normalizedError.message) {
        description = normalizedError.message
      }
      alertToast({
        title,
        description,
        ...(normalizedError.kind === "permission_denied"
          ? getApiErrorPresentationProps(e)
          : {}),
        showSupportAction: true,
      })
    } finally {
      setIsChangingPlan(false)
    }
  }, [
    organizationId,
    cancelScheduledChange,
    fetchOverview,
    alertToast,
    successToast,
    t,
  ])

  const handleBuyCredits = useCallback(
    async (credits: number) => {
      if (!organizationId || !currentPlan) return
      if (!isExtraCreditsCheckoutAllowed) {
        alertToast({
          title: t("billing.plan.errors.checkoutFailedTitle"),
          description: t("billing.plan.buyCreditsManagedUnavailable"),
        })
        return
      }
      try {
        setIsChangingPlan(true)
        const sessionResp = await createExtraCreditsSession({
          jsonBody: {
            organization_id: organizationId,
            plan_id: currentPlan.id,
            credits_purchased: credits,
            success_url: window.location.href,
            cancel_url: window.location.href,
          },
        })
        const checkoutUrl = sessionResp?.data?.checkout_url
        if (checkoutUrl) {
          window.location.href = checkoutUrl
        } else {
          alertToast({
            title: t("billing.plan.errors.checkoutFailedTitle"),
            description: t("billing.plan.errors.checkoutFailedDescription"),
            showSupportAction: true,
          })
        }
      } catch (e) {
        let title = t("billing.plan.errors.checkoutFailedTitle")
        let description = t("billing.plan.errors.checkoutFailedDescription")
        const normalizedError = normalizeApiError(e)
        const billingWarning = getBillingOperationWarningCopy(
          normalizedError.errorCode ?? undefined,
          t
        )
        if (normalizedError.messageKey) {
          description = t(normalizedError.messageKey)
        } else if (billingWarning) {
          title = billingWarning.title
          description = billingWarning.description
        } else if (normalizedError.message) {
          description = normalizedError.message
        }
        alertToast({
          title,
          description,
          ...(normalizedError.kind === "permission_denied"
            ? getApiErrorPresentationProps(e)
            : {}),
          showSupportAction: true,
        })
      } finally {
        setIsChangingPlan(false)
      }
    },
    [
      organizationId,
      currentPlan,
      isExtraCreditsCheckoutAllowed,
      createExtraCreditsSession,
      alertToast,
      t,
    ]
  )

  const handleExtraCreditsAction = useCallback((credits: number) => {
    setPendingExtraCredits(credits)
    setExtraCreditsConfirmOpen(true)
  }, [])

  const handleCreditUsagePointClick = useCallback(
    (date: string) => {
      if (!canDrillIntoCreditUsage) return
      setSelectedCreditUsageDate(date)
      setCreditUsageModalOpen(true)
    },
    [canDrillIntoCreditUsage]
  )

  return (
    <div className="space-y-6 relative">
      {isBusy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 rounded-xl bg-white px-6 py-5 text-slate-900 shadow-lg">
            <Loader2 className="h-6 w-6 animate-spin text-slate-700" />
            <div className="text-sm font-medium">
              {t("common.processing", { defaultValue: "Processing..." })}
            </div>
          </div>
        </div>
      )}
      <AlertBox
        title={t("billing.plan.downgradeLimitTitle")}
        description={downgradeWarningMessage}
        open={downgradeWarningOpen}
        confirmText={t("billing.plan.downgradeLimitConfirm")}
        cancelText={t("billing.plan.downgradeLimitClose")}
        onConfirm={() => setDowngradeWarningOpen(false)}
        onCancel={() => setDowngradeWarningOpen(false)}
        onOpenChange={setDowngradeWarningOpen}
      />
      <AlertBox
        title={t("billing.plan.planChangeConfirmTitle")}
        description={planChangeConfirmDescription}
        open={planChangeConfirmOpen}
        confirmText={t("billing.plan.planChangeConfirmConfirm")}
        cancelText={t("billing.plan.planChangeConfirmCancel")}
        onConfirm={confirmPlanAction}
        onCancel={() => setPlanChangeConfirmOpen(false)}
        onOpenChange={setPlanChangeConfirmOpen}
      />
      <AlertBox
        title={t("billing.plan.extraCreditsConfirmTitle")}
        description={t("billing.plan.extraCreditsConfirmDescription")}
        open={extraCreditsConfirmOpen}
        confirmText={t("billing.plan.extraCreditsConfirmConfirm")}
        cancelText={t("billing.plan.extraCreditsConfirmCancel")}
        onConfirm={() => {
          if (pendingExtraCredits == null) return
          setExtraCreditsConfirmOpen(false)
          void handleBuyCredits(pendingExtraCredits)
        }}
        onCancel={() => setExtraCreditsConfirmOpen(false)}
        onOpenChange={setExtraCreditsConfirmOpen}
      />
      <AlertBox
        title={t("billing.plan.cancelScheduledConfirmTitle")}
        description={t("billing.plan.cancelScheduledConfirmDescription")}
        open={cancelScheduledConfirmOpen}
        confirmText={t("billing.plan.cancelScheduledConfirmConfirm")}
        cancelText={t("billing.plan.cancelScheduledConfirmCancel")}
        onConfirm={() => {
          setCancelScheduledConfirmOpen(false)
          void cancelScheduledDowngrade()
        }}
        onCancel={() => setCancelScheduledConfirmOpen(false)}
        onOpenChange={setCancelScheduledConfirmOpen}
      />
      <BillingRulesDialog
        open={rulesOpen}
        onOpenChange={setRulesOpen}
        defaultSection={rulesSection}
      />
      {organizationId && (
        <CreditUsageRecordsModal
          open={creditUsageModalOpen}
          onOpenChange={setCreditUsageModalOpen}
          organizationId={organizationId}
          selectedDate={selectedCreditUsageDate}
          workspaceId={creditWorkspaceId}
        />
      )}

      <Card>
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-2xl font-semibold">
              {t("billing.plan.currentPlanTitle")}
            </CardTitle>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              {canViewLatestInvoice ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={() => void handleViewLatestInvoice()}
                  disabled={!organizationId || isOpeningLatestInvoice}
                >
                  {isOpeningLatestInvoice ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="mr-2 h-4 w-4" />
                  )}
                  {t("billing.plan.viewInvoice")}
                </Button>
              ) : null}
              {subscription?.canManagePaymentMethod ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={() => void handleManagePaymentMethod()}
                  disabled={!organizationId || isOpeningBillingPortal}
                >
                  {isOpeningBillingPortal ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="mr-2 h-4 w-4" />
                  )}
                  {t("billing.plan.managePaymentMethod")}
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <button
              type="button"
              onClick={() => {
                setRulesSection("plans")
                setRulesOpen(true)
              }}
              className="text-sm font-medium text-primary hover:underline"
            >
              {t("billing.plan.rules.viewRulesLink")}
            </button>
          </div>
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            <div className="space-y-1">
              <div className="text-muted-foreground">
                {t("billing.plan.currentPlanLabel")}
              </div>
              <div className="font-medium">
                {currentPlan?.displayName || t("billing.plan.noPlan")}
              </div>
            </div>
            {pendingPlan && (
              <div className="space-y-1">
                <div className="text-muted-foreground">
                  {isDbStagedFreemiumFallback
                    ? t("billing.plan.periodEndFallbackLabel")
                    : t("billing.plan.pendingPlanLabel")}
                </div>
                <div className="font-medium">
                  {isDbStagedFreemiumFallback
                    ? t("billing.plan.periodEndFallbackValue", {
                        plan: pendingPlan.displayName,
                        date: formatDate(subscription?.pendingEffectiveDate),
                      })
                    : t("billing.plan.pendingPlanValue", {
                        plan: pendingPlan.displayName,
                        date: formatDate(subscription?.pendingEffectiveDate),
                      })}
                </div>
                {isDbStagedFreemiumFallback ? (
                  <div className="text-xs text-muted-foreground">
                    {t("billing.plan.periodEndFallbackDescription")}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="text-xs font-medium text-amber-700 hover:underline dark:text-amber-400"
                    onClick={() => setCancelScheduledConfirmOpen(true)}
                    disabled={disablePlanChanges}
                  >
                    {t("billing.plan.cancelScheduledLink")}
                  </button>
                )}
              </div>
            )}
            {!pendingPlan && isPendingOperatorTakeover && (
              <div className="space-y-1">
                <div className="text-muted-foreground">
                  {t("billing.plan.pendingTakeoverLabel")}
                </div>
                <div className="font-medium">
                  {t("billing.plan.pendingTakeoverValue", {
                    date: formatDate(
                      subscription?.pendingTakeoverEffectiveDate
                    ),
                  })}
                </div>
              </div>
            )}
            <div className="space-y-1">
              <div className="text-muted-foreground">
                {t("billing.plan.currentStatusLabel")}
              </div>
              <div className="font-medium">
                {subscription ? statusLabel : "-"}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-muted-foreground">
                {t("billing.plan.currentRenewalLabel")}
              </div>
              <div className="font-medium">{renewalDate}</div>
            </div>
            <div className="space-y-1">
              <div className="text-muted-foreground">
                {t("billing.plan.currentPriceLabel")}
              </div>
              <div className="font-medium">
                {currentPlan
                  ? t("billing.plan.pricePerMonth", {
                      price: (
                        (currentPlan.prices.find(
                          (price) => price.billingPeriod === "monthly"
                        )?.amountCents ?? 0) / 100
                      ).toFixed(2),
                    })
                  : "-"}
              </div>
              {pendingPlan && !isDbStagedFreemiumFallback && (
                <div className="text-xs text-muted-foreground">
                  {t("billing.plan.prorationNotice")}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <div className="text-muted-foreground">
                {t("billing.plan.currentCreditsLabel")}
              </div>
              <div className="font-medium">
                {currentPlan?.includedCreditsMonthly ?? "-"}
              </div>
            </div>
          </div>
          {selfServePlanChangeBlocked ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <div className="font-medium">{selfServeBlockedTitle}</div>
              <div className="mt-1">{selfServeBlockedDescription}</div>
            </div>
          ) : null}
          {Boolean(subscription) && monthlyLimit > 0 && (
            <UsageBar
              creditsBalance={creditsBalance}
              monthlyLimit={monthlyLimit}
              title={t("billing.plan.currentCycleUsageTitle")}
              description={
                billingCycleRange
                  ? t("billing.plan.currentCycleUsageDescription", {
                      range: billingCycleRange,
                    })
                  : t("billing.plan.currentCycleUsageDescriptionNoRange")
              }
            />
          )}
          {planLimitUsageItems.length > 0 && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-foreground">
                {t("billing.plan.limitUsageTitle")}
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                {planLimitUsageItems.map((item) => (
                  <PlanLimitUsageBar key={item.key} item={item} />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card id="credit-usage-details">
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-semibold">
                {t("billing.plan.creditUsageTitle")}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t("billing.plan.creditUsageDescription")}
              </p>
              <p className="text-sm font-medium text-foreground">
                {t("billing.plan.creditUsageTotal", {
                  credits: formatCredits(creditUsage?.totalCredits ?? 0),
                })}
              </p>
              {canDrillIntoCreditUsage && creditUsageChartData.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t("billing.plan.creditUsageRecords.dailyHint")}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setIsCreditUsageExpanded((expanded) => !expanded)
                  }
                >
                  {isCreditUsageExpanded
                    ? t("billing.plan.hideCreditUsageDetails")
                    : t("billing.plan.showCreditUsageDetails")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const params = new URLSearchParams()
                    if (creditWorkspaceId !== "all") {
                      params.set("wid", creditWorkspaceId)
                    }
                    navigate(
                      params.toString()
                        ? `/dashboard?${params.toString()}`
                        : "/dashboard"
                    )
                  }}
                >
                  {t("billing.plan.backToDashboard")}
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        {isCreditUsageExpanded ? (
          <CardContent className="space-y-4 px-4 pb-6">
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              <Select
                value={creditGranularity}
                onValueChange={(value: "daily" | "weekly") =>
                  setCreditGranularity(value)
                }
              >
                <SelectTrigger className="w-full min-w-[160px]">
                  <SelectValue
                    placeholder={t("billing.plan.filters.granularity")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">
                    {t("billing.plan.filters.daily")}
                  </SelectItem>
                  <SelectItem value="weekly">
                    {t("billing.plan.filters.weekly")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={creditWorkspaceId}
                onValueChange={setCreditWorkspaceId}
              >
                <SelectTrigger className="w-full min-w-[180px]">
                  <SelectValue
                    placeholder={t("billing.plan.filters.workspace")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("billing.plan.filters.allWorkspaces")}
                  </SelectItem>
                  {organizationWorkspaces.map((workspace) => (
                    <SelectItem key={workspace.id} value={workspace.id}>
                      {workspace.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={creditStartDate}
                aria-label={t("billing.plan.filters.startDate")}
                max={creditEndDate || undefined}
                onChange={(event) => setCreditStartDate(event.target.value)}
              />
              <Input
                type="date"
                value={creditEndDate}
                aria-label={t("billing.plan.filters.endDate")}
                min={creditStartDate || undefined}
                onChange={(event) => setCreditEndDate(event.target.value)}
              />
            </div>
            <div className="h-[280px]">
              <CreditUsageTrendChartCard
                data={creditUsageChartData}
                isLoading={creditUsageStatus === FetchStatus.Loading}
                emptyLabel={t("billing.plan.creditUsageEmpty")}
                formatValue={formatCredits}
                yAxisWidth={42}
                canDrillIntoRecords={canDrillIntoCreditUsage}
                partialDate={getTodayDateOnly()}
                onDateClick={handleCreditUsagePointClick}
                embedded
              />
            </div>
          </CardContent>
        ) : null}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl font-semibold">
            {t("billing.plan.extraCreditsTitle")}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={t("billing.plan.rules.openRules")}
                    className="inline-flex items-center"
                  >
                    <HelpCircle className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>{t("billing.plan.rules.extraCreditsTooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground">
            {t("billing.plan.extraCreditsBalance", {
              balance: formatCredits(extraCreditsBalance),
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            {canBuyExtraCredits ? (
              packOptions.map((credits) => {
                const price = extraCreditUnitPrice
                  ? (credits * extraCreditUnitPrice).toFixed(2)
                  : null
                return (
                  <Button
                    key={credits}
                    variant="outline"
                    onClick={() => handleExtraCreditsAction(credits)}
                  >
                    {price
                      ? t("billing.plan.buyCreditsWithPrice", {
                          credits,
                          price,
                        })
                      : t("billing.plan.buyCredits", { credits })}
                  </Button>
                )
              })
            ) : (
              <Button variant="outline" disabled>
                {isExtraCreditsCheckoutAllowed
                  ? t("billing.plan.buyCreditsUnavailable")
                  : t("billing.plan.buyCreditsManagedUnavailable")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3 border-t pt-6">
        <div className="flex items-center gap-2">
          <h4 className="text-2xl font-semibold">
            {t("billing.plan.allPlansTitle")}
          </h4>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={t("billing.plan.rules.openRules")}
                  className="inline-flex items-center"
                >
                  <HelpCircle className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>{t("billing.plan.rules.allPlansTooltip")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {plans.map((plan) => {
          const monthlyPrice = plan.prices.find(
            (price) => price.billingPeriod === "monthly"
          )
          const isContactUs = plan.requiresSalesContact
          const priceText = isContactUs
            ? plan.contactEmail
              ? t("billing.plan.contactEmailValue", {
                  email: plan.contactEmail,
                })
              : t("billing.plan.contactUs")
            : monthlyPrice?.amountCents != null
              ? t("billing.plan.pricePerMonth", {
                  price: (monthlyPrice.amountCents / 100).toFixed(0),
                })
              : t("billing.plan.contactUs")
          const isCurrent = plan.id === currentPlan?.id
          const isPending = plan.id === pendingPlan?.id
          const limitLabel = (value?: number | null) =>
            value == null ? t("billing.plan.unlimited") : value.toString()
          const storageValue =
            plan.knowledgeBaseStorageBytesLimit == null
              ? t("billing.plan.unlimited")
              : formatBytes(plan.knowledgeBaseStorageBytesLimit)

          return (
            <Card
              key={plan.id}
              className="border-muted/60 transition duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{plan.displayName}</span>
                  {isCurrent ? (
                    <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground ring-1 ring-muted/40">
                      {t("billing.plan.current")}
                    </span>
                  ) : null}
                  {!isCurrent && isPending ? (
                    <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground ring-1 ring-muted/40">
                      {t("billing.plan.pending")}
                    </span>
                  ) : null}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex h-full flex-col space-y-4">
                <div
                  className={
                    isContactUs
                      ? "break-all text-sm font-medium text-muted-foreground"
                      : "text-2xl font-semibold"
                  }
                >
                  {priceText}
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span>•</span>
                    <span>
                      {t("billing.plan.limitsCredits", {
                        credits: limitLabel(plan.includedCreditsMonthly),
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>•</span>
                    <span>
                      {t("billing.plan.limitsWorkspacesValue", {
                        value: limitLabel(plan.workspaceLimit),
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>•</span>
                    <span>
                      {t("billing.plan.limitsMembersValue", {
                        value: limitLabel(plan.endUserLimit),
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>•</span>
                    <span>
                      {t("billing.plan.limitsStorage", {
                        size: storageValue,
                      })}
                    </span>
                  </div>
                </div>
                {isContactUs ? (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => handleContactEnterprise(plan)}
                    disabled={disablePlanChanges}
                    aria-busy={isChangingPlan}
                    style={{ marginTop: "auto" }}
                  >
                    {t("billing.plan.contactUsButton")}
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    variant={isCurrent ? "secondary" : "default"}
                    onClick={() => handlePlanAction(plan)}
                    disabled={isCurrent || isPending || disablePlanChanges}
                    aria-busy={isChangingPlan}
                    style={{ marginTop: "auto" }}
                  >
                    {isCurrent
                      ? t("billing.plan.current")
                      : isPending
                        ? t("billing.plan.pending")
                        : isChangingPlan
                          ? t("common.processing", {
                              defaultValue: "Processing...",
                            })
                          : t("billing.plan.select")}
                  </Button>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default OrganizationPlanTab
